import React, { useState, useRef, useEffect, useCallback } from 'react';
import { FaExpand, FaCompress, FaChevronDown, FaChevronRight } from 'react-icons/fa';
import { highlightHTMLHandlebars } from './syntaxHighlighting';

const HELPER_CATEGORIES = [
  {
    name: 'Data',
    color: 'text-emerald-400',
    helpers: [
      { label: 'Format Number', syntax: '{{formatNumber value}}', desc: 'Format with commas' },
      { label: 'Format Date', syntax: '{{formatDate value "YYYY-MM-DD"}}', desc: 'Format date' },
      { label: 'Format Weight', syntax: '{{formatWeight value}}', desc: 'Format as weight (kg)' },
      { label: 'Truncate', syntax: '{{truncate value 50}}', desc: 'Truncate text' },
    ],
  },
  {
    name: 'Logic',
    color: 'text-blue-400',
    helpers: [
      {
        label: '#each rows',
        syntax: '{{#each rows}}\n  <tr><td>{{this.ColumnName}}</td></tr>\n{{/each}}',
        desc: 'Loop over rows',
      },
      { label: '#if', syntax: '{{#if value}}\n  ...\n{{/if}}', desc: 'Conditional block' },
      {
        label: '#unless',
        syntax: '{{#unless value}}\n  ...\n{{/unless}}',
        desc: 'Inverse conditional',
      },
      { label: 'else', syntax: '{{else}}', desc: 'Else branch' },
    ],
  },
  {
    name: 'Comparison',
    color: 'text-amber-400',
    helpers: [
      {
        label: 'If Equals',
        syntax: '{{#ifEquals value "test"}}\n  ...\n{{/ifEquals}}',
        desc: 'Compare equal',
      },
      {
        label: 'If Greater',
        syntax: '{{#ifGreater value 100}}\n  ...\n{{/ifGreater}}',
        desc: 'Compare greater',
      },
      {
        label: 'If Less',
        syntax: '{{#ifLess value 50}}\n  ...\n{{/ifLess}}',
        desc: 'Compare less than',
      },
    ],
  },
  {
    name: 'HTML',
    color: 'text-rose-400',
    helpers: [
      {
        label: 'Table',
        syntax:
          '<table style="width:100%;border-collapse:collapse;">\n  <thead>\n    <tr style="background:#f3f4f6;">\n      <th style="padding:8px;border:1px solid #e5e7eb;text-align:left;">Column</th>\n    </tr>\n  </thead>\n  <tbody>\n    {{#each rows}}\n    <tr>\n      <td style="padding:8px;border:1px solid #e5e7eb;">{{this.value}}</td>\n    </tr>\n    {{/each}}\n  </tbody>\n</table>',
        desc: 'Full table structure',
      },
      {
        label: 'Row',
        syntax: '<tr><td style="padding:8px;border:1px solid #e5e7eb;">{{this.value}}</td></tr>',
        desc: 'Table row',
      },
      {
        label: 'Link',
        syntax: '<a href="{{url}}" style="color:#3b82f6;">{{text}}</a>',
        desc: 'Hyperlink',
      },
      { label: 'Bold', syntax: '<strong>{{value}}</strong>', desc: 'Bold text' },
    ],
  },
  {
    name: 'Meta',
    color: 'text-gray-400 dark:text-gray-600',
    helpers: [
      { label: 'Row Count', syntax: '{{meta.rowCount}}', desc: 'Total rows returned' },
      {
        label: 'Generated At',
        syntax: '{{meta.generatedAt}}',
        desc: 'Report generation timestamp',
      },
      { label: 'Query Name', syntax: '{{meta.queryName}}', desc: 'Name of the query' },
      { label: 'Report Name', syntax: '{{meta.reportName}}', desc: 'Name of the report' },
    ],
  },
];

const EmailTemplateEditor = ({ value, onChange, placeholder, minHeight = 300, label }) => {
  const textareaRef = useRef(null);
  const highlightRef = useRef(null);
  const lineNumberRef = useRef(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [lineCount, setLineCount] = useState(1);

  useEffect(() => {
    const lines = (value || '').split('\n').length;
    setLineCount(Math.max(lines, 1));
  }, [value]);

  const handleScroll = useCallback(() => {
    if (textareaRef.current && highlightRef.current) {
      highlightRef.current.scrollTop = textareaRef.current.scrollTop;
      highlightRef.current.scrollLeft = textareaRef.current.scrollLeft;
    }
    if (textareaRef.current && lineNumberRef.current) {
      lineNumberRef.current.scrollTop = textareaRef.current.scrollTop;
    }
  }, []);

  const handleKeyDown = (e) => {
    if (e.key === 'Tab') {
      e.preventDefault();
      const { selectionStart, selectionEnd } = e.target;
      const newValue = value.substring(0, selectionStart) + '  ' + value.substring(selectionEnd);
      onChange(newValue);
      requestAnimationFrame(() => {
        e.target.selectionStart = e.target.selectionEnd = selectionStart + 2;
      });
    }
  };

  const insertAtCursor = (text) => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    const { selectionStart, selectionEnd } = textarea;
    const newValue = value.substring(0, selectionStart) + text + value.substring(selectionEnd);
    onChange(newValue);
    requestAnimationFrame(() => {
      textarea.focus();
      textarea.selectionStart = textarea.selectionEnd = selectionStart + text.length;
    });
  };

  const editorHeight = isExpanded ? 'calc(100vh - 200px)' : `${minHeight}px`;

  const renderEditor = () => (
    <div className="relative" style={{ height: editorHeight }}>
      {/* Line numbers */}
      <div
        ref={lineNumberRef}
        className="absolute left-0 top-0 bottom-0 w-10 bg-[#282c34] border-r border-gray-700/30 dark:border-gray-200/30 overflow-hidden pointer-events-none z-10"
      >
        <div className="pt-3 pb-3">
          {Array.from({ length: lineCount }, (_, i) => (
            <div
              key={i}
              className="text-right pr-2 text-[11px] leading-5 text-gray-600 dark:text-gray-400 select-none font-mono"
            >
              {i + 1}
            </div>
          ))}
        </div>
      </div>

      {/* Syntax highlight layer */}
      <pre
        ref={highlightRef}
        className="absolute inset-0 pl-12 pt-3 pr-3 pb-3 overflow-auto font-mono text-[13px] leading-5 text-[#abb2bf] whitespace-pre-wrap break-words pointer-events-none"
        dangerouslySetInnerHTML={{ __html: highlightHTMLHandlebars(value) + '\n' }}
      />

      {/* Textarea */}
      <textarea
        ref={textareaRef}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        onScroll={handleScroll}
        onKeyDown={handleKeyDown}
        className="absolute inset-0 pl-12 pt-3 pr-3 pb-3 w-full h-full resize-none bg-transparent text-transparent caret-[#528bff] font-mono text-[13px] leading-5 outline-none border-0"
        placeholder={placeholder || 'Enter HTML template with Handlebars expressions...'}
        spellCheck={false}
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="off"
      />
    </div>
  );

  const renderHelperPalette = () => (
    <div className="bg-[#21252b] border-t border-gray-700/50 dark:border-gray-200/50 px-3 py-2 max-h-48 overflow-y-auto">
      {HELPER_CATEGORIES.map((cat) => (
        <div key={cat.name} className="mb-1">
          <button
            onClick={() => setExpandedCategory(expandedCategory === cat.name ? null : cat.name)}
            className="flex items-center space-x-1.5 text-[11px] font-medium w-full text-left py-0.5"
          >
            {expandedCategory === cat.name ? (
              <FaChevronDown className="w-2.5 h-2.5 text-gray-500" />
            ) : (
              <FaChevronRight className="w-2.5 h-2.5 text-gray-500" />
            )}
            <span className={cat.color}>{cat.name}</span>
          </button>
          {expandedCategory === cat.name && (
            <div className="grid grid-cols-2 xl:grid-cols-3 gap-1 mt-1 mb-2 pl-4">
              {cat.helpers.map((h, i) => (
                <button
                  key={i}
                  onClick={() => insertAtCursor(h.syntax)}
                  className="text-left px-2 py-1.5 rounded bg-gray-800/60 dark:bg-gray-100/60 hover:bg-gray-700/60 dark:hover:bg-gray-200/60 border border-gray-700/40 dark:border-gray-200/40 hover:border-gray-600/50 dark:hover:border-gray-300/50 transition-colors"
                  title={h.desc}
                >
                  <div className="text-[11px] font-medium text-gray-300 dark:text-gray-700">
                    {h.label}
                  </div>
                  <div className="text-[10px] font-mono text-gray-500 truncate">
                    {h.syntax.split('\n')[0]}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );

  if (isExpanded) {
    return (
      <div className="fixed inset-0 z-50 bg-[#282c34] flex flex-col">
        <div className="flex items-center justify-between px-4 py-2 bg-[#21252b] border-b border-gray-700/50 dark:border-gray-200/50">
          <span className="text-sm font-medium text-gray-300 dark:text-gray-700">
            {label || 'Email Template Editor'}
          </span>
          <button
            onClick={() => setIsExpanded(false)}
            className="text-gray-400 dark:text-gray-600 hover:text-gray-200 dark:hover:text-gray-800 p-1"
            title="Exit fullscreen"
          >
            <FaCompress />
          </button>
        </div>
        <div className="flex-1 min-h-0">{renderEditor()}</div>
        {renderHelperPalette()}
      </div>
    );
  }

  return (
    <div className="rounded-lg overflow-hidden border border-gray-700/50 dark:border-gray-200/50 bg-[#282c34]">
      <div className="flex items-center justify-between px-3 py-1.5 bg-[#21252b] border-b border-gray-700/50 dark:border-gray-200/50">
        <span className="text-[11px] font-medium text-gray-400 dark:text-gray-600">
          {label || 'HTML Template'}
        </span>
        <button
          onClick={() => setIsExpanded(true)}
          className="text-gray-500 hover:text-gray-300 dark:hover:text-gray-700 p-1"
          title="Expand editor"
        >
          <FaExpand className="w-3 h-3" />
        </button>
      </div>
      {renderEditor()}
      {renderHelperPalette()}
    </div>
  );
};

export default EmailTemplateEditor;
